import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { io } from 'socket.io-client';
import SecurityLanding from './SecurityLanding';
import '../styles/StudentView.css';
import { formatAnswer } from '../utils/answerFormatter';

const API_BASE_URL = process.env.NODE_ENV === 'production' 
  ? window.location.origin
  : process.env.REACT_APP_API_URL || 'http://localhost:5001';

function StudentView() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const socketRef = useRef(null);
  
  const [question, setQuestion] = useState(null);
  const [answer, setAnswer] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [clientCount, setClientCount] = useState(0);
  const [sessionActive, setSessionActive] = useState(true);
  const [loading, setLoading] = useState(true);
  const [showSecurityLanding, setShowSecurityLanding] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);
  const answerRef = useRef(null);
  const inactivityTimerRef = useRef(null);
  const sessionExpiryTimerRef = useRef(null);

  // SESSION EXPIRATION: 60 minutes
  const SESSION_EXPIRY_TIME = 60 * 60 * 1000; // 60 minutes in milliseconds

  useEffect(() => {
    // Validate session exists and hasn't expired
    const validateSession = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/sessions/${sessionId}`);
        const data = await response.json();
        
        if (!response.ok) {
          // Check if it's a 410 (Gone - session ended)
          if (response.status === 410 || data.isExpired === true) {
            if (data.message === 'Session has ended') {
              console.log('⚠ Session ended by trainer');
              setSessionActive(false);
            } else {
              console.log('⚠ Session link has expired');
              setSessionExpired(true);
              setSessionActive(false);
            }
          } else {
            // Session not found (404) or other error
            console.log('⚠ Session ended - not found on server');
            setSessionActive(false);
          }
          return;
        }
        
        // Check if session link has expired (60 minutes)
        if (data.data.isExpired) {
          console.log('⚠ Session link has expired after 60 minutes');
          setSessionExpired(true);
          setSessionActive(false);
          return;
        }
        
        setQuestion(data.data.question);
        setAnswer(data.data.answer);
        setClientCount(data.data.clientCount);
      } catch (error) {
        console.error('Error validating session:', error);
        setSessionActive(false);
      } finally {
        setLoading(false);
      }
    };

    validateSession();

    // Set session expiry timer - auto-logout after SESSION_EXPIRY_TIME
    sessionExpiryTimerRef.current = setTimeout(() => {
      console.log('⚠ Session link expired after 60 minutes');
      setSessionExpired(true);
      setSessionActive(false);
    }, SESSION_EXPIRY_TIME);

    // Connect to Socket.IO
    const socket = io(API_BASE_URL || 'http://localhost:5001', {
      query: {
        sessionId,
        role: 'student'
      },
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: 5
    });

    socket.on('connect', () => {
      console.log('✓ Connected to session:', sessionId);
      setIsConnected(true);
      
      // Request current content
      socket.emit('student:request-current');
    });

    socket.on('student:answer-updated', (data) => {
      console.log('✓ Answer updated');
      setQuestion(data.question);
      setAnswer(data.answer);
      
      // Update page title with question
      if (data.question) {
        document.title = `${data.question} - Find QA Answers`;
      }
      
      // Auto-scroll to latest content
      setTimeout(() => {
        if (answerRef.current) {
          answerRef.current.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    });

    socket.on('student:answer-cleared', () => {
      console.log('✓ Answer cleared');
      setQuestion(null);
      setAnswer(null);
      document.title = 'Find QA Answers - FQA';
    });

    socket.on('student:current-content', (data) => {
      console.log('✓ Received current content');
      setQuestion(data.question);
      setAnswer(data.answer);
    });

    socket.on('client-joined', (data) => {
      console.log('✓ Client joined. Total:', data.clientCount);
      setClientCount(data.clientCount);
    });

    socket.on('client-left', (data) => {
      console.log('✓ Client left. Remaining:', data.clientCount);
      setClientCount(data.clientCount);
    });

    socket.on('session:ended', () => {
      console.log('⚠ Session has ended by trainer');
      setSessionActive(false);
      setIsConnected(false);
    });

    socket.on('disconnect', () => {
      console.log('⚠ Disconnected from session');
      setIsConnected(false);
    });

    socket.on('error', (error) => {
      console.error('Socket error:', error);
    });

    socketRef.current = socket;

    // Periodic check to validate session is still active (every 5 seconds)
    const sessionCheckInterval = setInterval(async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/sessions/${sessionId}`);
        if (!response.ok) {
          // Session ended or not found
          console.log('⚠ Session validation failed - session ended or not found');
          setSessionActive(false);
          socket.disconnect();
        } else {
          const data = await response.json();
          if (data.data.isExpired) {
            // Session link expired (60 minutes)
            console.log('⚠ Session link expired during validation');
            setSessionExpired(true);
            setSessionActive(false);
            socket.disconnect();
          }
        }
      } catch (error) {
        console.error('Session validation error:', error);
      }
    }, 5000);

    return () => {
      clearInterval(sessionCheckInterval);
      if (sessionExpiryTimerRef.current) {
        clearTimeout(sessionExpiryTimerRef.current);
      }
      if (socket) {
        socket.disconnect();
      }
    };
  }, [sessionId, navigate]);

  // Auto-scroll when answer updates
  useEffect(() => {
    if (answer && answerRef.current) {
      answerRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [answer]);

  // Visibility API - Keep connection alive when tab switches
  useEffect(() => {
    const handleVisibilityChange = () => {
      console.log('Visibility changed - document.hidden:', document.hidden);
      // Keep connection alive - no need to show security page
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Keep connection alive - no need for blur handlers
  useEffect(() => {
    // Connection stays active regardless of window focus
    return () => {};
  }, []);

  // Inactivity timer - keep connection alive
  const resetInactivityTimer = () => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }
    // No timeout needed - keep connection alive indefinitely
  };

  useEffect(() => {
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    
    const handleActivity = () => {
      resetInactivityTimer();
    };

    events.forEach(event => {
      window.addEventListener(event, handleActivity);
    });

    resetInactivityTimer();

    return () => {
      events.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
    };
  }, []);

  const handleStartShopping = () => {
    setShowSecurityLanding(false);
    document.title = 'Find QA Answers - FQA';
    navigate(`/session/${sessionId}`);
    resetInactivityTimer();
  };

  if (loading) {
    return (
      <div className="student-view">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading session...</p>
        </div>
      </div>
    );
  }

  if (showSecurityLanding) {
    return <SecurityLanding onStart={handleStartShopping} />;
  }

  if (sessionExpired) {
    return (
      <div className="student-view">
        <div className="session-ended-container">
          <div className="session-ended-icon">⏱️</div>
          <h2>Session Link Expired</h2>
          <p>This session link is no longer valid.</p>
          <p style={{ fontSize: '0.9rem', color: '#999', marginTop: '1rem' }}>Session links expire after 60 minutes for security.</p>
          <button onClick={() => {
            navigate('/');
          }} className="btn-return" style={{ marginTop: '2rem' }}>
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  if (!sessionActive) {
    return (
      <div className="student-view">
        <div className="session-ended-container">
          <div className="session-ended-icon">🔒</div>
          <h2>Session Ended</h2>
          <p>The trainer has ended this session.</p>
          <p style={{ fontSize: '0.9rem', color: '#999', marginTop: '1rem' }}>You no longer have access to this content.</p>
          <button onClick={() => {
            navigate('/');
          }} className="btn-return" style={{ marginTop: '2rem' }}>
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="student-view">
      {/* Header with connection status */}
      <div className="student-header">
        <div className="session-info">
          <span className={`connection-status ${isConnected ? 'connected' : 'disconnected'}`}>
            {isConnected ? '🔴 Live' : '⚪ Offline'}
          </span>
          <span className="client-count">
            {clientCount} {clientCount === 1 ? 'student' : 'students'} viewing
          </span>
        </div>
      </div>

      {/* Main content area */}
      <div className="student-content">
        {/* Question section */}
        {question && (
          <div className="question-section">
            <h2 className="question-title">{question}</h2>
            <div className="question-divider"></div>
          </div>
        )}

        {/* Answer section */}
        {answer ? (
          <div className="answer-section" ref={answerRef}>
            <div className="answer-content">
              <div className="answer-body" dangerouslySetInnerHTML={{ __html: formatAnswer(answer) }} />
            </div>
          </div>
        ) : (
          <div className="empty-answer">
            {isConnected ? (
              <>
                <div className="empty-icon">📺</div>
                <p>Waiting for trainer to share content...</p>
              </>
            ) : (
              <>
                <div className="empty-icon">⚠️</div>
                <p>Connecting to session...</p>
              </>
            )}
          </div>
        )}
      </div>

      {/* Footer info */}
      <div className="student-footer">
        <p>Session ID: {sessionId}</p>
      </div>
    </div>
  );
}

export default StudentView;
